

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BackButton from '../../components/common/BackButton';

const G = '#2E7D32';

const CATEGORIES = [
  { key:'vegetables',   label:'Vegetables',   img:'/icons/vegetables.png',      emoji:'🥕' },
  { key:'green_leaves', label:'Green Leaves',  img:'/icons/green-leaves.png',      emoji:'' },
  { key:'flowers',      label:'Flowers',       img:'/icons/flowers.png',     emoji:'🌸' },
  // { key:'fruits',       label:'Fruits',        img:'/icons/fruits.jpg',      emoji:'🍎' },
  // { key:'herbs',        label:'Herbs',         img:'/icons/seeds.jpg',       emoji:'' },
  // { key:'other',        label:'Other',         img:'/icons/fertilizers.png', emoji:'' },
];

const UNITS = ['acre','gunta','sqft'];
const UNIT_LABELS = { acre:'Acre', gunta:'Gunta', sqft:'Square Feet' };

export default function AddCropPage() {
  const navigate    = useNavigate();
  const fileRef     = useRef();
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos,    setPhotos]    = useState([]);
  const [form, setForm] = useState({
    cropName:        '',
    category:        '',
    subCategory:     '',
    landArea:        '',
    landAreaUnit:    'acre',
    description:     '',
    locationAddress: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openGallery = () => {
    if (photos.length >= 5) return toast.error('You can add up to 5 photos only');
    fileRef.current?.click();
  };

  const uploadPhotos = async (files) => {
    if (!files.length) return;
    const remaining = 5 - photos.length;
    if (remaining <= 0) return toast.error('You can add up to 5 photos only');
    const selected = Array.from(files);
    if (selected.length > remaining) {
      toast.error(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} allowed — added the first ${remaining}`);
    }
    setUploading(true);
    try {
      const fd = new FormData();
      selected.slice(0, remaining).forEach(f => fd.append('files', f));
      const { data } = await api.post('/upload/media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (!data.files?.length) { toast.error('Upload failed — no photos returned'); return; }
      setPhotos(p => [...p, ...data.files.map(f => f.url)].slice(0, 5));
      toast.success('Photos uploaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.cropName.trim())    return toast.error('Crop name is required');
    if (!form.category)           return toast.error('Please select a category');
    if (!form.subCategory.trim()) return toast.error('Sub category is required');
    if (photos.length === 0)      return toast.error('Please upload at least 1 photo');
    if (!form.landArea.trim())    return toast.error('Land area is required');
    const areaNum = parseFloat(form.landArea);
    if (isNaN(areaNum) || areaNum <= 0) return toast.error('Enter a valid land area greater than 0');
    if (!form.landAreaUnit)       return toast.error('Please select a unit for the land area');
    setSaving(true);
    try {
      await api.post('/crops', {
        cropName:        form.cropName.trim(),
        category:        form.category.toLowerCase(),
        subCategory:     form.subCategory.trim(),
        startDate:       new Date().toISOString().split('T')[0],
        expectedDays:    90,
        description:     form.description.trim(),
        locationAddress: form.locationAddress.trim(),
        mediaUrls:       photos,
        landArea:        areaNum,
        landAreaUnit:    form.landAreaUnit,
      });
      toast.success('Crop added! ');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add crop');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background:'#F0F7F0', minHeight:'100vh', fontFamily:'Inter,system-ui,sans-serif', paddingBottom:'calc(64px + env(safe-area-inset-bottom, 0px) + 24px)' }}>

      {/* Header */}
      <div style={{ background:`linear-gradient(135deg,#2E7D32 0%,#388E3C 60%,#43A047 100%)`, padding:'14px 16px 22px', position:'relative', overflow:'hidden' }}>
        <BackButton style={{ marginBottom:10, position:'relative', zIndex:2 }} />
        <div style={{ position:'absolute', right:0, top:0, bottom:0, width:'120%', overflow:'hidden', pointerEvents:'none', zIndex:1 }}>
          <img src="/images/plant-hero.png" alt=""
            style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center bottom' }}
            onError={e => { e.target.style.display='none'; }} />
        </div>
        <div style={{ position:'relative', zIndex:2 }}>
          <div style={{ fontSize:22, fontWeight:800, color:'white', marginBottom:3 }}>Add New Crop</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,.8)' }}>Fill details and add photos</div>
          <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:10 }}>
            <div style={{ height:3, width:50, background:'rgba(255,255,255,.9)', borderRadius:2 }} />
            <div style={{ height:3, width:25, background:'rgba(255,255,255,.35)', borderRadius:2 }} />
            <span style={{ fontSize:13 }}></span>
          </div>
        </div>
      </div>

      <div style={{ padding:'14px 12px', display:'flex', flexDirection:'column', gap:12 }}>

        {/* Crop Name */}
        <div style={{ background:'white', borderRadius:16, padding:'14px', border:'0.5px solid #E5E7EB' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            {/* <div style={{ width:36, height:36, borderRadius:'50%', background:'#EAF3DE', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 }}></div> */}
            <span style={{ fontSize:14, fontWeight:700, color:'#1a1a1a' }}>Crop Name <span style={{ color:'#EF4444' }}>*</span></span>
          </div>
          <input value={form.cropName} onChange={e => set('cropName', e.target.value)}
            placeholder="e.g. Tomato, Rice, Mango"
            style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', color:'#374151' }}
            onFocus={e => e.target.style.borderColor=G} onBlur={e => e.target.style.borderColor='#E5E7EB'} />
          <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:52, height:52, borderRadius:10, background:'#EAF3DE', border:'1px solid #E5E7EB', flexShrink:0 }} />
            <button type="button" onClick={openGallery}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 16px', borderRadius:20, border:`1.5px solid ${G}`, background:'white', color:G, fontSize:12.5, fontWeight:700, cursor: photos.length >= 5 ? 'not-allowed' : 'pointer', opacity: photos.length >= 5 ? .5 : 1 }}>
              📹 Upload Photo
            </button>
          </div>
          <div style={{ marginTop:6, fontSize:11, color:'#9CA3AF' }}>
            Add up to 5 photos <span style={{ color:'#EF4444' }}>*</span>{uploading && <span style={{ marginLeft:6, color:G, fontWeight:600 }}>Uploading...</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:'none' }}
            onChange={e => uploadPhotos(e.target.files)} />
          {photos.length > 0 && (
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginTop:10 }}>
              {photos.map((p,i) => (
                <div key={i} style={{ width:52, height:52, borderRadius:8, overflow:'hidden', position:'relative' }}>
                  <img src={p} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  <button onClick={() => setPhotos(ps => ps.filter((_,idx)=>idx!==i))}
                    style={{ position:'absolute', top:2, right:2, width:16, height:16, borderRadius:'50%', background:'#EF4444', border:'none', color:'white', fontSize:10, cursor:'pointer' }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category */}
        <div style={{ background:'white', borderRadius:16, padding:'14px', border:'0.5px solid #E5E7EB' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <span style={{ fontSize:18 }}></span>
            <span style={{ fontSize:14, fontWeight:700, color:'#1a1a1a' }}>Category <span style={{ color:'#EF4444' }}>*</span></span>
          </div>
          <div style={{ display:'flex', gap:8, overflowX:'auto', scrollbarWidth:'none', paddingBottom:4 }}>
            {CATEGORIES.map(cat => {
              const active = form.category === cat.key;
              return (
                <div key={cat.key} onClick={() => set('category', cat.key)}
                  style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', gap:5, cursor:'pointer' }}>
                  <div style={{ width:72, height:72, borderRadius:14, overflow:'hidden', border:`2.5px solid ${active?G:'#E5E7EB'}`, background:'#EAF3DE', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <img src={cat.img} alt={cat.label}
                      style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                      onError={e => { e.target.style.display='none'; }} />
                    {active && (
                      <div style={{ position:'absolute', top:4, right:4, width:20, height:20, borderRadius:'50%', background:G, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'white', zIndex:2, fontWeight:700 }}>✓</div>
                    )}
                  </div>
                  <span style={{ fontSize:10, fontWeight:active?700:500, color:active?G:'#6B7280', textAlign:'center', lineHeight:1.2, maxWidth:72 }}>{cat.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sub Category */}
        <div style={{ background:'white', borderRadius:16, padding:'14px', border:'0.5px solid #E5E7EB' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <span style={{ fontSize:18 }}></span>
            <span style={{ fontSize:14, fontWeight:700, color:'#1a1a1a' }}>Sub Category <span style={{ color:'#EF4444' }}>*</span></span>
          </div>
          <input value={form.subCategory} onChange={e => set('subCategory', e.target.value)}
            placeholder="e.g. Cherry Tomato, Basmati Rice..."
            style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', color:'#374151' }}
            onFocus={e => e.target.style.borderColor=G} onBlur={e => e.target.style.borderColor='#E5E7EB'} />
        </div>

        {/* Land Area + Unit */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div style={{ background:'white', borderRadius:16, padding:'13px', border:'0.5px solid #E5E7EB' }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
              <span style={{ fontSize:16 }}>🏔️</span>
              <span style={{ fontSize:12, fontWeight:700, color:'#1a1a1a' }}>Land Area <span style={{ color:'#EF4444' }}>*</span></span>
            </div>
            <input type="number" value={form.landArea} onChange={e => set('landArea', e.target.value)}
              placeholder="e.g. 2" min={0} step={0.1}
              style={{ width:'100%', padding:'9px 10px', border:'1.5px solid #E5E7EB', borderRadius:9, fontSize:12, outline:'none', boxSizing:'border-box', color:'#374151' }}
              onFocus={e => e.target.style.borderColor=G} onBlur={e => e.target.style.borderColor='#E5E7EB'} />
          </div>
          <div style={{ background:'white', borderRadius:16, padding:'13px', border:'0.5px solid #E5E7EB' }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
              <span style={{ fontSize:16 }}>📐</span>
              <span style={{ fontSize:12, fontWeight:700, color:'#1a1a1a' }}>Unit <span style={{ color:'#EF4444' }}>*</span></span>
            </div>
            <div style={{ position:'relative' }}>
              <select value={form.landAreaUnit} onChange={e => set('landAreaUnit', e.target.value)}
                style={{ width:'100%', padding:'9px 26px 9px 10px', border:'1.5px solid #E5E7EB', borderRadius:9, fontSize:12, outline:'none', appearance:'none', background:'white', color:'#374151', boxSizing:'border-box', cursor:'pointer' }}>
                {UNITS.map(u => <option key={u} value={u}>{UNIT_LABELS[u]}</option>)}
              </select>
              <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#9CA3AF', pointerEvents:'none' }}>▾</span>
            </div>
          </div>
        </div>

        {/* Description */}
        <div style={{ background:'white', borderRadius:16, padding:'13px', border:'0.5px solid #E5E7EB' }}>
          <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
            <span style={{ fontSize:16 }}>📝</span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1a1a1a' }}>Description</span>
          </div>
          <textarea value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="Soil type, variety, farming method..."
            rows={3}
            style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:12, outline:'none', resize:'none', boxSizing:'border-box', color:'#374151', fontFamily:'inherit' }}
            onFocus={e => e.target.style.borderColor=G} onBlur={e => e.target.style.borderColor='#E5E7EB'} />
        </div>

        {/* Field Location */}
        <div style={{ background:'white', borderRadius:16, padding:'13px', border:'0.5px solid #E5E7EB' }}>
          <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
            <span style={{ fontSize:16 }}></span>
            <span style={{ fontSize:13, fontWeight:700, color:'#1a1a1a' }}>Field Location</span>
            <span style={{ fontSize:11, color:'#9CA3AF', fontWeight:400 }}>(optional)</span>
          </div>
          <div style={{ position:'relative' }}>
            <input value={form.locationAddress} onChange={e => set('locationAddress', e.target.value)}
              placeholder="Village, District, State"
              style={{ width:'100%', padding:'10px 38px 10px 12px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:12, outline:'none', boxSizing:'border-box', color:'#374151' }}
              onFocus={e => e.target.style.borderColor=G} onBlur={e => e.target.style.borderColor='#E5E7EB'} />
            <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:18, pointerEvents:'none' }}>🗺️</span>
          </div>
        </div>

        {/* Add Crop Button */}
        <button onClick={save} disabled={saving}
          style={{ width:'100%', padding:'16px', background:saving?'#9CA3AF':G, color:'white', border:'none', borderRadius:16, fontSize:15, fontWeight:500, cursor:saving?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:saving?'none':'0 4px 16px rgba(46,125,50,.4)', marginTop:2, boxSizing:'border-box' }}>
          <span style={{ fontSize:16 }}></span>
          {saving ? 'Adding Crop...' : 'Add Crop'}
        </button>

      </div>
    </div>
  );
}